import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDocs, doc, collection, query, where, updateDoc, deleteDoc } from 'firebase/firestore';
import { DaysBetweenDates } from '../../functions/DaysBetweenDates';
import { db } from '../../firebase';
import moment from 'moment';

import './styles/AbsencesReport.css';

const departmentsName = {
  IT: 'Informática',
  IDE: 'Proyectos internacionales',
  ELE: 'Electrónica',
  MAT: 'Manteriales',
  TEC: 'Tecnología de procesos',
  IPR: 'Ingeniería de Proyectos',
  FOR: 'Formación',
  INF: 'Comunicación',
  ADM: 'Administración',
  DIR: 'Dirección',
};

export const AbsencesPanel = () => {
  const [users, setUsers] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [activeAbsence, setActiveAbsence] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());

  const navigate = useNavigate();
  document.querySelector('body').style.overflow = 'hidden';

  useEffect(() => {
    (async () => {
      const userQuery = query(collection(db, 'users'), where('estadoSesion', '!=', 'Deshabilitado'));
      const calendarQuery = query(collection(db, 'calendars'), where('year', '==', year.toString()));

      if (userQuery && calendarQuery) {
        const users = await getDocs(userQuery);
        const calendars = await getDocs(calendarQuery);

        var arrUsers = [];
        var arrCalendars = calendars.docs.map((cal) => ({ id: cal.id, ...cal.data() }));

        for (const user of users.docs) {
          let absencesList = [];
          let absences = user.data()?.calendario?.[year]?.horas_variables || 0;
          let persAffairs = user.data()?.calendario?.[year]?.asuntos_propios || 0;

          await getDocs(query(collection(db, 'users', user.id, 'ausencias'), where('status', '==', 'Aceptado'), where('year', '==', year.toString()))).then((documents) => {
            documents.forEach((doc) => {
              absencesList.push({ id: doc.id, ...doc.data() });
              if (doc.data().type === 'Vacaciones') {
                absences = absences - doc.data().total;
              } else if (doc.data().type === 'Asuntos_propios') {
                persAffairs = persAffairs - (moment(doc.data().endDate, 'DD/MM/YYYY').diff(moment(doc.data().startDate, 'DD/MM/YYYY'), 'days') + 1);
              }
            });
          });

          // Ordenar absencesList por startDate (más antiguas primero)
          absencesList.sort((a, b) => {
            const dateA = moment(a.startDate, 'DD/MM/YYYY');
            const dateB = moment(b.startDate, 'DD/MM/YYYY');
            return dateA.diff(dateB); // asc (más antiguas primero)
          });

          arrUsers.push({
            uid: user.id,
            displayName: user.data().displayName,
            photoURL: user.data().photoURL,
            departament: user.data().departamento,
            tAbsences: user.data()?.calendario?.[year]?.horas_variables,
            absences: absences,
            tPersAffairs: user.data()?.calendario?.[year]?.asuntos_propios,
            persAffairs: persAffairs,
            absencesList: absencesList,
            calendar: arrCalendars.find((cal) => cal.id === user.data()?.calendario?.[year]?.id) || null,
          });
        }

        arrUsers.sort((a, b) => (a.displayName > b.displayName ? 1 : b.displayName > a.displayName ? -1 : 0));
        setUsers(arrUsers);
      }
    })();
  }, [year]);

  const onChangeYear = (e) => {
    const min = e.target.min;

    setUsers([]);
    setActiveUser(null);

    if (e.target.value < min) {
      setYear(min);
    } else {
      setYear(e.target.value);
    }
  };

  const onChangeActiveAbsenceDate = (field, value) => {
    setActiveAbsence({ ...activeAbsence, [field]: value });

    //Calcula el total si se cambian las fechas
    if (field === 'startDate' || field === 'endDate') {
      const calendar = activeUser.calendar.data;
      const start = field === 'startDate' ? value : activeAbsence.startDate;
      const end = field === 'endDate' ? value : activeAbsence.endDate;

      const days = DaysBetweenDates(moment(start, 'DD/MM/YYYY'), moment(end, 'DD/MM/YYYY'));
      var total = 0;

      days.forEach((day) => {
        const month = moment(day, 'DD/MM/YYYY').format('MMMM');
        const dayNumber = moment(day, 'DD/MM/YYYY').format('DD') - 1;

        const hours = calendar[month].days[dayNumber].hours;
        total = total + parseInt(hours.split(':')[0] * 60) + parseInt(hours.split(':')[1]);
      });

      setActiveAbsence((prevAbsence) => ({ ...prevAbsence, total: total }));
    }
  };

  const updateAbsence = async () => {
    // Lógica para actualizar la ausencia en la base de datos
    try {
      //Actualiza en la base de datos la ausencia editada
      const absenceRef = doc(db, 'users', activeUser.uid, 'ausencias', activeAbsence.id);
      await updateDoc(absenceRef, {
        startDate: activeAbsence.startDate,
        endDate: activeAbsence.endDate,
        total: activeAbsence.total,
      });

      // Busca en activeUser.absencesList y actualiza la ausencia correspondiente por uid
      const index = activeUser.absencesList.findIndex((absence) => absence.id === activeAbsence.id);
      if (index !== -1) {
        activeUser.absencesList[index] = activeAbsence;
      }

      //Vuelve a calcular los totales de ausencias y asuntos propios
      let absences = 0;
      let persAffairs = 0;
      activeUser.absencesList.forEach((absence) => {
        if (absence.type === 'Vacaciones') {
          absences += absence.total;
        } else if (absence.type === 'Asuntos_propios') {
          persAffairs += moment(absence.endDate, 'DD/MM/YYYY').diff(moment(absence.startDate, 'DD/MM/YYYY'), 'days') + 1;
        }
      });
      activeUser.absences = activeUser.tAbsences - absences;
      activeUser.persAffairs = activeUser.tPersAffairs - persAffairs;

      setActiveUser({ ...activeUser, absencesList: activeUser.absencesList });
    } catch (error) {
      console.error('Error updating absence: ', error);
    }

    setActiveAbsence(null);
    hideModal();
  };

  const removeAbsence = async (absenceId) => {
    // Lógica para eliminar la ausencia en la base de datos
    try {
      //preguntar con un alert de confirmación
      if (!window.confirm('¿Estás seguro de que deseas eliminar esta ausencia?')) {
        return;
      }

      //Elimina en la base de datos la ausencia seleccionada
      const absenceRef = doc(db, 'users', activeUser.uid, 'ausencias', absenceId);
      await deleteDoc(absenceRef);

      // Actualiza el estado local para reflejar la eliminación
      const updatedAbsencesList = activeUser.absencesList.filter((absence) => absence.id !== absenceId);

      //Vuelve a calcular los totales de ausencias y asuntos propios
      let absences = 0;
      let persAffairs = 0;
      updatedAbsencesList.forEach((absence) => {
        if (absence.type === 'Vacaciones') {
          absences += absence.total;
        } else if (absence.type === 'Asuntos_propios') {
          persAffairs += moment(absence.endDate, 'DD/MM/YYYY').diff(moment(absence.startDate, 'DD/MM/YYYY'), 'days') + 1;
        }
      });

      activeUser.absences = activeUser.tAbsences - absences;
      activeUser.persAffairs = activeUser.tPersAffairs - persAffairs;

      setActiveUser({ ...activeUser, absencesList: updatedAbsencesList });
    } catch (error) {
      console.error('Error removing absence: ', error);
    }
  };

  const exit = () => {
    document.querySelector('body').style.overflow = 'auto';
    navigate('/');
  };

  const showModal = (absence) => {
    setActiveAbsence(absence);
    document.getElementById('workCalendar-modal').classList.toggle('disabled');
  };

  const hideModal = () => {
    setActiveAbsence(null);
    document.getElementById('workCalendar-modal').classList.toggle('disabled');
  };

  console.log(activeUser);
  console.log(activeAbsence);

  return (
    <div className='modal-route'>
      <div className='absencesReport'>
        <div className='modal-header'>
          <h5 className='modal-title '>
            <i className='fa-solid fa-umbrella-beach'></i>Control de ausencias y vacaciones
          </h5>
          <button type='button' className='btn-close' onClick={() => exit()}></button>
        </div>
        <div className='modal-body'>
          <div className='row'>
            <div className='col-md-4 margin-right px-4 border-end '>
              <div className='mb-3'>
                <label htmlFor='year' className='form-label'>
                  Seleccionar año:
                </label>
                <input type='number' className='form-control' id='year' value={year} min={'2023'} onChange={(e) => onChangeYear(e)} onKeyDown={(e) => e.preventDefault()} />
              </div>

              <hr />
              {users.length > 0 ? (
                <div className='d-flex flex-column gap-3 overflow-auto' style={{ maxHeight: '62vh' }}>
                  {users.map((user) => (
                    <div
                      key={user.uid}
                      type='button'
                      className={`user d-flex align-items-center justify-content-between border-0 rounded-lg p-2 bg-light ${activeUser?.uid === user.uid ? 'active' : ''}`}
                      onClick={() => setActiveUser(user)}
                    >
                      <div className='d-flex align-items-center justify-content-start gap-3'>
                        <img src={user.photoURL} alt='user-avatar' className='absencesReport avatar' />
                        <div className='d-flex flex-column gap-1'>
                          <span className='name'>{user.displayName}</span>
                          <span className='badge bg-white text-dark px-2 '>{departmentsName[user.departament]}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='d-flex align-items-center justify-content-center h-100'>
                  <span className='text-muted'>Cargando...</span>
                </div>
              )}
            </div>

            <div className='col-md-8 px-4'>
              {activeUser ? (
                <>
                  <div className='row'>
                    <h6 className='mb-0'>{activeUser.displayName}</h6>
                  </div>
                  <hr />
                  <div className='d-flex gap-3 mt-4 ' key={activeUser.uid}>
                    <div className='card col-md-6 mb-4 px-0 border-0'>
                      <div className='card-header'>
                        <h6 className='card-title float-start mb-0'>Horas variables</h6>
                      </div>
                      <div className='card-body d-flex flex-column'>
                        <div className='progress rounded-pill mt-2 mb-3 float-start'>
                          <div
                            className='progress-bar progress-bar-striped bg-success rounded-pill '
                            role='progressbar'
                            style={{ width: (activeUser.absences / activeUser.tAbsences) * 100 + '%' }}
                          ></div>
                        </div>
                        <div className='d-flex justify-content-center'>
                          <span>
                            {Math.floor(activeUser.absences / 60) + 'h ' + (activeUser.absences % 60) + 'min'}{' '}
                            <strong>{'/ ' + Math.floor(activeUser.tAbsences / 60) + 'h ' + (activeUser.tAbsences % 60) + 'min'}</strong>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className='card col-md-6 mb-4 px-0 border-0'>
                      <div className='card-header'>
                        <h6 className='card-title float-start mb-0'>Asuntos propios</h6>
                      </div>
                      <div className='card-body d-flex flex-column'>
                        <div className='progress rounded-pill mt-2 mb-3 float-start'>
                          <div
                            className='progress-bar progress-bar-striped bg-primary rounded-pill '
                            role='progressbar'
                            style={{ width: (activeUser.persAffairs / activeUser.tPersAffairs) * 100 + '%' }}
                          ></div>
                        </div>
                        <div className='d-flex justify-content-center'>
                          <span>
                            {activeUser.persAffairs + ' días'} <strong>{'/ ' + activeUser.tPersAffairs + ' días'}</strong>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {activeUser.absencesList.length > 0 ? (
                    <div className='table-responsive overflow-auto' style={{ maxHeight: '50vh' }}>
                      <table className='table table-hover'>
                        <thead className='bg-light'>
                          <tr>
                            <th className='text-start'>Tipo</th>
                            <th className='text-start'>Fecha inicio</th>
                            <th className='text-start'>Fecha fin</th>
                            <th className='text-start'>Total</th>
                            <th className='text-start'>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeUser.absencesList.map((absence, index) => (
                            <tr key={index}>
                              <td
                                className={`text-start ${
                                  absence.type === 'Vacaciones'
                                    ? 'text-success'
                                    : absence.type === 'Enfermedad'
                                    ? 'text-danger'
                                    : absence.type === 'Evento'
                                    ? 'text-warning'
                                    : 'text-primary'
                                }`}
                              >
                                {absence.type === 'Vacaciones'
                                  ? 'Vacaciones'
                                  : absence.type === 'Enfermedad'
                                  ? 'Enfermedad'
                                  : absence.type === 'Evento'
                                  ? 'Evento'
                                  : 'Asuntos propios'}
                              </td>
                              <td className='text-start'>{absence.startDate}</td>
                              <td className='text-start'>{absence.endDate}</td>
                              <td className='text-start'>{Math.floor(absence.total / 60) + 'h ' + (absence.total % 60) + 'min'}</td>
                              <td className='d-flex align-items-center justify-content-center gap-2'>
                                <div
                                  className=' button d-flex bg-primary px-2 align-items-center justify-content-center
                                 gap-2 rounded-3 text-white '
                                  onClick={() => showModal(absence)}
                                >
                                  <i class='fa-regular fa-pen-to-square text-white  fs-6'></i>
                                  <span className='text-white fs-6'>Editar</span>
                                </div>
                                <div className='button' onClick={() => removeAbsence(absence.id)}>
                                  <i class=' bg-danger p-1 fa-regular text-white fa-trash-can rounded-3'></i>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <span className='text-muted'>No hay ausencias registradas para este usuario en el año seleccionado.</span>
                  )}
                </>
              ) : (
                <div className='d-flex align-items-center justify-content-center h-100'>
                  <span className='text-muted'>Seleccione un usuario para ver su informe de ausencias</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className='modal disabled' id='workCalendar-modal'>
        <div className='modal-dialog'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h1 className='modal-title fs-5' id='exampleModalLabel'>
                Editar ausencia
              </h1>
              <button type='button' className='btn-close' onClick={() => hideModal()}></button>
            </div>
            <div className='modal-body'>
              <form
                className='mt-2'
                onSubmit={(e) => {
                  e.preventDefault();
                  updateAbsence();
                }}
              >
                <div className='mb-3'>
                  <label htmlFor='type' className='form-label'>
                    Tipo de ausencia:
                  </label>
                  <select className='form-select' id='type' disabled>
                    <option value={activeAbsence?.type === 'Vacaciones' ? 'Vacaciones' : activeAbsence?.type === 'Enfermedad' ? 'Enfermedad' : 'Asuntos_propios'}>
                      {activeAbsence?.type === 'Vacaciones' ? 'Vacaciones' : activeAbsence?.type === 'Enfermedad' ? 'Enfermedad' : 'Asuntos propios'}
                    </option>
                  </select>
                </div>
                <div className='mb-3'>
                  <label htmlFor='reason' className='form-label'>
                    Motivo:
                  </label>
                  <input type='text' className='form-control' id='reason' defaultValue={activeAbsence?.description} disabled />
                </div>
                <div className='mb-3'>
                  <label htmlFor='startDate' className='form-label'>
                    Fecha de inicio:
                  </label>
                  <input
                    type='date'
                    className='form-control'
                    id='startDate'
                    value={activeAbsence?.startDate.split('/').reverse().join('-')}
                    onChange={() => onChangeActiveAbsenceDate('startDate', document.getElementById('startDate').value.split('-').reverse().join('/'))}
                  />
                </div>
                <div className='mb-3'>
                  <label htmlFor='endDate' className='form-label'>
                    Fecha de fin:
                  </label>
                  <input
                    type='date'
                    className='form-control'
                    id='endDate'
                    value={activeAbsence?.endDate.split('/').reverse().join('-')}
                    onChange={() => onChangeActiveAbsenceDate('endDate', document.getElementById('endDate').value.split('-').reverse().join('/'))}
                  />
                </div>
                <div className='mb-3'>
                  <label htmlFor='total' className='form-label'>
                    Total:
                  </label>
                  <div className='form-control' id='total' style={{ backgroundColor: '#e9ecef' }}>
                    {Math.floor(activeAbsence?.total / 60) + 'h ' + (activeAbsence?.total % 60) + 'min'}
                  </div>
                </div>
                <div className='d-flex justify-content-end'>
                  <button type='button' className='btn btn-secondary me-2' onClick={() => hideModal()}>
                    Cancelar
                  </button>
                  <button type='submit' className='btn btn-success'>
                    Guardar cambios
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
